import { ref } from 'vue'
import { TableDataQuery, getData } from '@/app/core/services/get-table'
import { Field } from '@/app/core/services/forms'
import { validateTranslate } from '@/app/core/composable/general'
import {
  camelToSnake,
  isUUID,
  snakeToCamelObject
} from '@/app/core/util/strings'
import * as XLSX from 'xlsx-js-style'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { formatNumber } from '@/app/core/util/numbers'
import { useI18n } from 'vue-i18n'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useNotificationsStore } from '@/store/notifications'
dayjs.extend(utc)

export default function useDynamicTable() {
  const loadingData = ref(false)
  const search = ref<string>()
  const columns = ref<any[]>([])
  const field = ref<Field>()
  const rows = ref<any[]>([])
  const perPage = ref(0)
  const offset = ref(0)
  const totalRows = ref(0)
  const sortBy = ref('')
  const { t } = useI18n()

  /* Datos componente Modal y Loading Reports */
  const queryReport = ref([])
  const showModal = ref(false)
  const titleModal = ref('')
  const descriptionModal = ref('')
  const typeAlertModal = ref('')
  const notifications = useNotificationsStore()

  const loadingReport = ref(false)
  const messageLoading = ref('')
  const urlFile = ref(null)
  const refresh = ref()

  const loadData = async (currentId: string) => {
    if (!field.value?.query || !currentId) return
    loadingData.value = true

    let where: string[] = [...(field.value?.query?.where ?? [])]
    if (search.value) {
      where.push(
        `CONCAT(${(
          field.value?.query?.fields
            ?.filter(
              (_: string) =>
                !_.split(' as ')?.[0]
                  .toLowerCase()
                  .match(/(^id$|_id$|.id$)/)
            )
            .map((field: string) => field.split(' as ')?.[0] ?? field) ?? []
        ).join(',')}) ILIKE '%${search.value}%'`
      )
    }
    where = where.map(_ => _.replaceAll(/:current/g, `'${currentId ?? ''}'`))

    let modifier: string[] = [...(field.value?.query?.modifier ?? [])]

    if (sortBy.value) {
      modifier = modifier.filter(_ => !_.toUpperCase().includes('ORDER'))

      const sortQueryField = field.value?.query?.fields
        ?.find(_ => _.split(' as ')?.[1] === sortBy.value?.[1])
        ?.split(' as ')?.[0]

      modifier.push(
        `ORDER BY ${camelToSnake(sortQueryField || sortBy.value?.[1])} ${
          sortBy.value?.[0]
        }`
      )
    }

    const hasOrderBy = modifier.some((_: string) =>
      _.toUpperCase().includes('ORDER')
    )

    if (!hasOrderBy) {
      const orderName = field.value?.query?.fields
        ?.filter((_: string) => _ !== 'id')
        .map((field: string) => {
          if (field.split(' as ')?.[0].split('.')[1] === undefined) {
            return field.split(' as ')?.[0].split('.')[1]
          } else {
            return field.split(' as ')?.[0].split('.')[0]
          }
        })

      if (orderName.indexOf('name') >= 1) modifier.push(`ORDER BY name DESC`)
    }

    try {
      const query: TableDataQuery = {
        tables: [...(field.value?.query?.tables ?? [])],
        join: [...(field.value?.query?.join ?? [])],
        fields: [...(field.value?.query?.fields ?? [])],
        where,
        options: {
          outputFormat: 'json',
          pagination: {
            limit: perPage.value,
            offset: offset.value
          }
        },
        modifier
      }
      const data: any = await getData(query)
      rows.value = data[0].results
      queryReport.value = data[1]
      totalRows.value = data[0].totalItems
      // fix dates format
      formatDates('DD/MM/YYYY')
      formatNumbers(rows.value)
    } finally {
      loadingData.value = false
    }
  }

  const formatNumbers = (values: any) => {
    values.forEach((row: any) => {
      Object.keys(row).forEach((key: string) => {
        if (
          key === 'identificationNumber' ||
          key === 'accountNumber' ||
          key === 'account_number' ||
          key === 'code' ||
          key === 'name' ||
          key === 'description'
        )
          return
        if (key === 'percentage' && row[key] && !String(row[key]).includes('%'))
          row[key] = row[key] + '%'
        //TODO: Agregar llave a las columnas para identificar que es un número y no sea de manera general
        if (row[key]?.toString().match(/^-?\d{1,10}(\.\d+)?$/gi)) {
          row[key] = formatNumber(row[key])
        }
      })
    })
  }

  const formatDates = (format: string) => {
    rows.value.forEach((row: any) => {
      Object.keys(row).forEach((key: string) => {
        const isDate = key.match(
          /[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])/gi
        )
        if (row[key] && isDate && dayjs(row[key]).isValid()) {
          row[key] = dayjs(row[key]).format(format)
        }
      })
    })
  }
  const formatDatesField = (obj: any) => {
    Object.keys(obj).forEach((key: string) => {
      if (typeof obj[key] !== 'string') return
      const isDate = obj[key].match(
        /[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])/gi
      )
      if (obj[key] && isDate && dayjs(obj[key]).isValid()) {
        obj[key] = dayjs.utc(obj[key]).format('YYYY-MM-DD')
      }
    })
    return obj
  }

  const loadRow = async (id: string, currentId?: string) => {
    try {
      let recordId = id
      if (field.value?.query?.groupedBy) {
        recordId = currentId || id
      }

      const query: TableDataQuery = {
        tables: [...(field.value?.query?.tables ?? [])],
        fields: ['*'],
        where: [`id = '${recordId}'`]
      }
      const data: any = await getData(query)
      let results = data[0]?.results[0] ?? []
      if (field.value?.query?.groupedBy) {
        results =
          results[field.value?.query?.groupedBy].find(
            (_: any) => _?.id === id
          ) ?? []
      }
      return formatDatesField(snakeToCamelObject(results))
    } catch (error: any) {
      console.error(error)
    }
  }

  const setField = (f: Field) => {
    field.value = f ?? {}
    setColumns()
  }

  const setColumns = () => {
    columns.value.push(
      ...(field.value?.props?.columns
        .map(
          (col: {
            name: string
            title: string
            replace: string | null
            options: object[] | null
          }) => {
            return {
              name: col.name,
              title: validateTranslate(col.title),
              table: col.name,
              replace: col.replace,
              options: Array.isArray(col.options)
                ? col.options.map((opt: any) => ({
                    id: opt.id,
                    name: opt.name
                  }))
                : null
            }
          }
        )
        ?.filter((_: any) => _.title !== 'ID') ?? []),
      { name: 'actions', title: '' }
    )
  }

  const getTableName = () => {
    return field.value?.query?.tables?.[0].replace('pg_', '')
  }

  const replaceIdsByName = async (obj: any) => {
    const newObj: any = {}
    for (const key in obj) {
      const column = columns.value?.find((_: any) => _.name === key)
      if (column?.replace && obj[key] !== null) {
        if (isUUID(obj[key])) {
          const [table, ...fields] = column.replace.split('.')
          const query: TableDataQuery = {
            tables: ['pg_'.concat(table)],
            fields: [fields.join('.')],
            where: ["id = '".concat(obj[key]).concat("'")]
          }
          const data: any = await getData(query)
          const result = data[0]?.results?.[0]
          if (result) {
            const value = Object.values(result)[0]
            newObj[key] = value
          } else {
            newObj[key] = obj[key]
          }
        } else {
          newObj[key] = obj[key]
        }
      } else if (column?.options) {
        newObj[key] =
          column.options.find((option: any) => option.id === obj[key])?.name ||
          obj[key]
      } else {
        newObj[key] = obj[key]
      }
    }
    return newObj
  }

  const closeModalReport = () => {
    showModal.value = false
    urlFile.value = null
  }

  const download = async () => {
    try {
      loadingReport.value = true
      messageLoading.value = t('loading.report')
      urlFile.value = null
      const data = {
        code: 'configurations/entity-table',
        parameters: {
          query: JSON.stringify(queryReport.value),
          fields: field.value!.query!.fields.filter(
            field => !field.includes('id')
          ),
          headers: columns.value
            .filter(field => field.title !== '')
            .map(field => field.title),
          module: field
            .value!.label.normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
        }
      }
      const id = await generateReportList(data)
      refresh.value = setInterval(async () => {
        const response = await getReportRequest(id)
        if (response.status === 'Completado') {
          urlFile.value = response.url
          descriptionModal.value = ''
          typeAlertModal.value = 'success'
          titleModal.value = t('reports.notification.reportGenerated')
          showModal.value = true
          loadingReport.value = false
          clearInterval(refresh.value)
        } else if (response.status === 'Error') {
          typeAlertModal.value = 'error'
          titleModal.value = t('reports.notification.reportNoGenerated')
          descriptionModal.value = response.errors
          showModal.value = true
          loadingReport.value = false
          clearInterval(refresh.value)
        }
      }, 5000)
    } catch (error: any) {
      notificationError(
        error,
        field.value!.label,
        t,
        notifications,
        loadingReport
      )
    }
  }

  return {
    loadingData,
    search,
    columns,
    rows,
    perPage,
    offset,
    totalRows,
    sortBy,
    loadData,
    loadRow,
    setField,
    setColumns,
    getTableName,
    replaceIdsByName,
    download,
    formatNumbers,
    loadingReport,
    messageLoading,
    showModal,
    typeAlertModal,
    titleModal,
    descriptionModal,
    closeModalReport,
    urlFile
  }
}
